import React from 'react';

/**
 * Multi-OES radar chart component showing all OESes on one chart
 * @param {Object} props
 * @param {Array} props.data - Array of {oes_id, oes_name, by_function: {DomainName: percentage, ...}}
 * @param {number} props.size - Size of the chart in pixels (default: 500)
 */
export default function MultiOESRadarChart({ data = [], size = 500 }) {
  const center = size / 2;
  const radius = size * 0.35; // Chart radius (leaving padding)

  // Derive domain names from the first OES that has by_function data (framework-agnostic)
  const firstWithData = data.find(d => d.by_function && Object.keys(d.by_function).length > 0);
  const functions = firstWithData ? Object.keys(firstWithData.by_function) : ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];
  const numAxes = functions.length;
  const angleStep = (2 * Math.PI) / numAxes;
  
  // Generate colors for each OES (using a color palette)
  const colors = [
    '#0066B3', '#E87722', '#6B4E9B', '#10b981', '#ef4444', '#f59e0b',
    '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
  ];
  
  // Calculate points for each axis
  const axes = functions.map((func, index) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start from top
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return { func, angle, x, y, index };
  });
  
  // Calculate data points for each OES
  const oesPaths = data.map((oes, oesIndex) => {
    const scores = functions.map(func => oes.by_function[func] || 0);
    const dataPoints = axes.map((axis, index) => {
      const scoreRadius = (scores[index] / 100) * radius;
      const x = center + scoreRadius * Math.cos(axis.angle);
      const y = center + scoreRadius * Math.sin(axis.angle);
      return { x, y };
    });
    
    const pathData = dataPoints.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ') + ' Z';
    
    return {
      oes_id: oes.oes_id,
      oes_name: oes.oes_name,
      pathData,
      dataPoints,
      color: colors[oesIndex % colors.length],
      scores,
    };
  });
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
        {/* Grid circles */}
        {[0.25, 0.5, 0.75, 1.0].map((scale, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={radius * scale}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="1"
            strokeDasharray={i === 3 ? "none" : "2,2"}
          />
        ))}
        
        {/* Axes lines */}
        {axes.map((axis, index) => (
          <line
            key={index}
            x1={center}
            y1={center}
            x2={axis.x}
            y2={axis.y}
            stroke="#cbd5e1"
            strokeWidth="1"
          />
        ))}
        
        {/* OES data areas (filled polygons) - render in reverse order so first OES is on top */}
        {oesPaths.slice().reverse().map((oes, index) => (
          <path
            key={oes.oes_id}
            d={oes.pathData}
            fill={oes.color}
            fillOpacity="0.15"
            stroke={oes.color}
            strokeWidth="2"
          />
        ))}
        
        {/* OES data points */}
        {oesPaths.map((oes) => (
          oes.dataPoints.map((point, index) => (
            <circle
              key={`${oes.oes_id}-${index}`}
              cx={point.x}
              cy={point.y}
              r="3"
              fill={oes.color}
              stroke="white"
              strokeWidth="1.5"
            />
          ))
        ))}
        
        {/* Function labels */}
        {axes.map((axis, index) => {
          const labelRadius = radius * 1.15; // Slightly outside the chart
          const labelX = center + labelRadius * Math.cos(axis.angle);
          const labelY = center + labelRadius * Math.sin(axis.angle);
          
          return (
            <text
              key={index}
              x={labelX}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-semibold fill-slate-700"
            >
              {/* Native framework domains can be long; truncate with full text on hover */}
              <title>{axis.func}</title>
              {axis.func.length > 22 ? `${axis.func.slice(0, 21)}…` : axis.func}
            </text>
          );
        })}
      </svg>
      
      {/* Legend */}
      {oesPaths.length > 0 && (
        <div className="mt-4 flex flex-wrap justify-center gap-4">
          {oesPaths.map((oes) => (
            <div key={oes.oes_id} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: oes.color }}
              />
              <span className="text-xs font-medium text-slate-700">{oes.oes_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
